"use client";

/**
 * Plugin SDK — Registry singleton.
 *
 * Phase 25-AB Task 15: Plugin SDK foundation.
 *
 * The registry is the in-process broker between plugins and the rest of
 * makemonlab Studio. It is intentionally synchronous and side-effect free
 * apart from in-memory maps so it works on both the server and the client.
 */

import type { SelectionSnapshot, StudioAPI } from "./api";
import type { StudioPlugin } from "./plugin";
import type {
	NotifyLevel,
	PanelSlot,
	PluginCommand,
	PluginExporter,
	PluginPanel,
	PluginWizard,
} from "./types";

export class PluginRegistry implements StudioAPI {
	private panels = new Map<string, PluginPanel>();
	private commands = new Map<string, PluginCommand>();
	private exporters = new Map<string, PluginExporter>();
	private wizards = new Map<string, PluginWizard>();
	private plugins = new Map<string, StudioPlugin>();

	registerPanel(panel: PluginPanel): () => void {
		this.panels.set(panel.id, panel);
		return () => {
			this.panels.delete(panel.id);
		};
	}

	registerCommand(command: PluginCommand): () => void {
		this.commands.set(command.id, command);
		return () => {
			this.commands.delete(command.id);
		};
	}

	registerExporter(exporter: PluginExporter): () => void {
		this.exporters.set(exporter.key, exporter);
		return () => {
			this.exporters.delete(exporter.key);
		};
	}

	registerWizard(wizard: PluginWizard): () => void {
		this.wizards.set(wizard.key, wizard);
		return () => {
			this.wizards.delete(wizard.key);
		};
	}

	getSelection(): SelectionSnapshot {
		// Read-only stub. Future task will wire this to the timeline store.
		return { clipIds: [] };
	}

	notify(message: string, level: NotifyLevel = "info"): void {
		if (typeof console !== "undefined") {
			const tag = `[plugin notify ${level}]`;
			if (level === "error") {
				console.error(tag, message);
			} else if (level === "warn") {
				console.warn(tag, message);
			} else {
				console.log(tag, message);
			}
		}
	}

	async activate(plugin: StudioPlugin): Promise<void> {
		const { id } = plugin.manifest;
		if (this.plugins.has(id)) {
			console.warn(`[plugin-sdk] ${id} already activated, skipping`);
			return;
		}
		await plugin.activate(this);
		this.plugins.set(id, plugin);
	}

	async deactivate(pluginId: string): Promise<void> {
		const plugin = this.plugins.get(pluginId);
		if (!plugin) return;
		if (plugin.deactivate) await plugin.deactivate();
		this.plugins.delete(pluginId);
	}

	listPanels(): PluginPanel[] {
		return Array.from(this.panels.values());
	}

	listCommands(): PluginCommand[] {
		return Array.from(this.commands.values());
	}

	listExporters(): PluginExporter[] {
		return Array.from(this.exporters.values());
	}

	listWizards(): PluginWizard[] {
		return Array.from(this.wizards.values());
	}

	listPlugins(): StudioPlugin[] {
		return Array.from(this.plugins.values());
	}

	panelsBySlot(slot: PanelSlot): PluginPanel[] {
		return this.listPanels().filter((p) => p.slot === slot);
	}

	/** Test-only reset hook. Removes every plugin + registration. */
	reset(): void {
		this.panels.clear();
		this.commands.clear();
		this.exporters.clear();
		this.wizards.clear();
		this.plugins.clear();
	}
}

/** Singleton registry instance. */
export const registry = new PluginRegistry();
